import { type ReactNode, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { type MDXEditorMethods } from '@mdxeditor/editor';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';
import {
  MarkdownEditor,
  type MarkdownEditorProps,
} from '@/components/MarkdownEditor/MarkdownEditor';

import { type FieldCommonProps } from '../FormField.types';

export type FieldMarkdownEditorProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'markdown';
  label?: ReactNode;
  helper?: ReactNode;
} & Pick<
  MarkdownEditorProps,
  'placeholder' | 'autoFocus' | 'contentEditableClassName' | 'className'
> &
  FieldCommonProps<TFieldValues, TName>;

export const FieldMarkdownEditor = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldMarkdownEditorProps<TFieldValues, TName>
) => {
  const markdownRef = useRef<MDXEditorMethods>(null);

  return (
    <Controller
      {...props}
      render={({ field }) => {
        const { value, ...fieldProps } = field;

        markdownRef.current?.setMarkdown(value ?? '');

        return (
          <FormFieldItem>
            {!!props.label && <FormFieldLabel>{props.label}</FormFieldLabel>}
            <Box
              borderRadius="lg"
              border="1px"
              borderColor="gray.200"
              background="white"
            >
              <MarkdownEditor
                placeholder={props.placeholder}
                autoFocus={props.autoFocus}
                contentEditableClassName={props.contentEditableClassName}
                className={props.className}
                markdown={value ?? ''}
                {...fieldProps}
                ref={markdownRef}
              />
            </Box>
            {!!props.helper && (
              <FormFieldHelper>{props.helper}</FormFieldHelper>
            )}
            <FormFieldError />
          </FormFieldItem>
        );
      }}
    />
  );
};
